import { VesselArea } from '@operations/app/api'
import { PriorityChip } from '@operations/components/chips/PriorityChip'
import { WorkInstructionStatusChip } from '@operations/components/chips/WorkInstructionStatusChip'
import { convertOperationType } from '@operations/utils/helper'
import { TFnType } from '@tolgee/react'
import { getMultiSelectRenderBadge, TableDateCell, TomTableEmptyCell, YesNoBadge } from '@tom-ui/ui'
import { ContainerNumber, formatDateRange } from '@tom-ui/utils'
import _ from 'lodash'
import { MRT_ColumnDef } from 'material-react-table'
import { WorkInstructionWorkQueueCell } from '../components/atoms/WorkInstructionWorkQueueCell'
import {
  ControlRoomWorkTabType,
  WorkInstructionViewModel,
} from '../models/work-instruction-view.model'
import { WorkInstructionUIStore } from '../stores/work-instruction.ui-store'
import { workInstructionColumnFilterPresets } from './workIntructionColumnFilterConfigs'

export const getWorkInstructionColumns = (
  t: TFnType,
  uiStore: WorkInstructionUIStore,
  selectedTabType: ControlRoomWorkTabType,
): MRT_ColumnDef<WorkInstructionViewModel>[] => {
  const columns: MRT_ColumnDef<WorkInstructionViewModel>[] = [
    {
      ...workInstructionColumnFilterPresets.searchMandatory(),
      accessorKey: 'originDescription',
      header: t('origin', 'Origin'),
      accessorFn: row => mapLocationDescription(row, 'origin', uiStore, t),
      size: 200,
      Cell: TomTableEmptyCell,
    },
    {
      ...workInstructionColumnFilterPresets.searchMandatory(),
      accessorKey: 'destinationDescription',
      header: t('destination', 'Destination'),
      accessorFn: row => mapLocationDescription(row, 'destination', uiStore, t),
      size: 200,
      Cell: TomTableEmptyCell,
    },
  ]

  if (selectedTabType === 'vessel') {
    columns.push(
      {
        ...workInstructionColumnFilterPresets.searchOptional(),
        accessorKey: 'wqName',
        header: t('wQName', 'WQ Name'),
        accessorFn: row => {
          if (row.workQueueName) return row.workQueueName

          if (!row.workQueueFromTime || !row.workQueueToTime) return null

          return `${t(convertOperationType(row.operationType, row.carrierType))} ${row.craneName} ${formatDateRange(row.workQueueFromTime, row.workQueueToTime)}, ${!row.bay ? t('unknownBay', 'Unknown bay') : t('bay', 'Bay') + ' ' + row.bay} - ${t(_.lowerFirst(row.vesselArea ?? VesselArea.Hold))}`
        },
        maxSize: 150,
        size: 150,
        Cell: cell => <WorkInstructionWorkQueueCell workInstruction={cell.row.original} />,
      },
      {
        ...workInstructionColumnFilterPresets.vesselOperationTypeSelect(t),
        accessorKey: 'operationType',
        header: t('process', 'Process'),
        accessorFn: row => t(convertOperationType(row.operationType, row.carrierType)),
        size: 120,
      },
      {
        ...workInstructionColumnFilterPresets.numericMandatory(),
        accessorKey: 'workQueueOrder',
        header: t('sequence', 'Sequence'),
        size: 120,
        Cell: TomTableEmptyCell,
      },
      {
        ...workInstructionColumnFilterPresets.numericMandatory(),
        accessorKey: 'bay',
        header: t('bay', 'Bay'),
        accessorFn: row => {
          if (!row.bay) return null
          return row.bay > 0 ? row.bay : null
        },
        size: 100,
        Cell: TomTableEmptyCell,
      },
      {
        ...workInstructionColumnFilterPresets.vesselAreaSelect(t),
        accessorKey: 'vesselArea',
        header: t('section', 'Section'),
        accessorFn: row => (row.vesselArea ? t(_.lowerFirst(row.vesselArea)) : null),
        size: 100,
        Cell: TomTableEmptyCell,
      },
      {
        ...workInstructionColumnFilterPresets.yesNoSelect(),
        accessorKey: 'approved',
        header: t('approved', 'Approved'),
        size: 80,
        accessorFn: row => (row.isApproved ? t('yes', 'Yes') : t('no', 'No')),
        Cell: cell => <YesNoBadge isYes={!!cell.row.original.isApproved} />,
      },
    )
  } else if (selectedTabType === 'truck') {
    columns.push(
      {
        ...workInstructionColumnFilterPresets.truckOperationTypeSelect(t),
        accessorKey: 'operationType',
        header: t('process', 'Process'),
        accessorFn: row => t(convertOperationType(row.operationType, row.carrierType)),
        size: 120,
      },
      {
        ...workInstructionColumnFilterPresets.searchMandatory(),
        accessorKey: 'carrierName',
        header: t('carrier', 'Carrier'),
        size: 200,
        Cell: TomTableEmptyCell,
      },
    )
  } else if (selectedTabType === 'train') {
    columns.push({
      ...workInstructionColumnFilterPresets.trainOperationTypeSelect(t),
      accessorKey: 'operationType',
      header: t('process', 'Process'),
      accessorFn: row => t(convertOperationType(row.operationType, row.carrierType)),
      size: 120,
    })
  } else if (selectedTabType === 'internal') {
    columns.push({
      ...workInstructionColumnFilterPresets.internalOperationTypeSelect(t),
      accessorKey: 'operationType',
      header: t('process', 'Process'),
      accessorFn: row => t(convertOperationType(row.operationType, row.carrierType)),
      size: 120,
    })
  }

  columns.push(
    {
      ...workInstructionColumnFilterPresets.statusMultiSelect(t),
      accessorKey: 'status',
      header: t('wiStatus', 'WI status'),
      size: 120,
      Cell: cell => <WorkInstructionStatusChip status={cell.row.original.status} />,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by Status')),
    },
    {
      ...workInstructionColumnFilterPresets.priorityMultiSelect(t),
      accessorKey: 'isPriority',
      header: t('priority', 'Priority'),
      size: 120,
      Cell: cell => <PriorityChip isPriority={cell.row.original.isPriority} />,
      muiFilterTextFieldProps: ({ column }) =>
        getMultiSelectRenderBadge(column, t, t('filterByPriority', 'Filter by Priority')),
    },
    {
      ...workInstructionColumnFilterPresets.searchMandatory(),
      accessorKey: 'transportUnitNumber',
      header: t('unit', 'Unit'),
      size: 170,
      Cell: ({ renderedCellValue }) =>
        renderedCellValue ? (
          <ContainerNumber value={String(renderedCellValue)} format={false} />
        ) : (
          <TomTableEmptyCell renderedCellValue={renderedCellValue} />
        ),
    },
    {
      ...workInstructionColumnFilterPresets.searchOptional(),
      accessorKey: 'lastEquipment',
      header: t('lastEquipment', 'Last equipment'),
      size: 170,
      Cell: TomTableEmptyCell,
    },
    {
      ...workInstructionColumnFilterPresets.dateMandatory(),
      accessorKey: 'plannedTime',
      header: t('plannedTime', 'Planned time'),
      size: 120,
      Cell: cell => <TableDateCell date={cell.row.original.plannedTime} timeStyle='short' />,
    },
    {
      ...workInstructionColumnFilterPresets.dateOptional(),
      accessorKey: 'executionTime',
      header: t('executionTime', 'Execution time'),
      size: 170,
      Cell: cell => <TableDateCell date={cell.row.original.executionTime} timeStyle='short' />,
    },
    {
      ...workInstructionColumnFilterPresets.yesNoSelect(),
      header: t('hasHolds', 'Has holds'),
      size: 80,
      accessorFn: row => (row.hasActiveHolds ? t('yes', 'Yes') : t('no', 'No')),
      Cell: cell => (
        <YesNoBadge
          isYes={!!cell.row.original.hasActiveHolds}
          isWarning={cell.row.original.hasActiveHolds}
        />
      ),
    },
  )

  return columns
}

const mapLocationDescription = (
  workInstruction: WorkInstructionViewModel,
  direction: 'origin' | 'destination',
  uiStore: WorkInstructionUIStore,
  t: TFnType,
) => {
  const locationDescription =
    direction === 'origin'
      ? workInstruction.originDescription
      : workInstruction.destinationDescription

  if (workInstruction.journeyCategory !== 'Restow') {
    return locationDescription ? t(locationDescription) : null
  }

  const inboundWI = uiStore.workInstructions.find(
    wi =>
      wi.transportUnitNumber === workInstruction.transportUnitNumber &&
      wi.operationType === 'Inbound',
  )

  if (
    ((direction === 'origin' && workInstruction.operationType === 'Outbound') ||
      (direction === 'destination' && workInstruction.operationType === 'Inbound')) &&
    ['Todo', 'Pending'].includes(inboundWI?.status ?? '')
  ) {
    return t('vesselFullName', 'Vessel {name}', { name: workInstruction.carrierName }) ?? null
  }

  return locationDescription ? t(locationDescription) : null
}
